import React from 'react';

import Button from '../../../../library/button/button';
import MinimumPriceMessage from '../../../../components/minimum-price-message/minimum-price-message';
import Price from '../../../../components/price/price';
import ProductDescription from '../../../../components/product-description/product-description';
import ProductFlags from '../../../../components/product-flags/product-flags';
import ProductImage from '../../../../components/product-image/product-image';
import ProductSecondaryFlags from '../../../../components/product-secondary-flags/product-secondary-flags';
import StackedMarkdownPrice from '../../../../components/stacked-markdown-price/stacked-markdown-price';
import WinterTireBadge from '../../../../components/winter-tire-badge/winter-tire-badge';
import { TimesRegularIcon } from '../../../../shared/icons/icons';

import {
  useMediaQuery,
  useMyAccount,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import {
  getIsNoLongerAvailable,
  getPriceInfo,
  getPrioritizedArticleStatusProduct,
  getShouldShowMinimumPriceMessage,
  isWinterTire
} from '../../../../shared/utils/product/product';

import { ProductData } from '../../../../shared/types/graphql-types';

import './product-compare-preview-summary.scss';

interface ProductComparePreviewSummaryProps {
  className?: string;
  onClearProduct?: (product: ProductData) => void;
  product: ProductData;
}

function ProductComparePreviewSummary(
  props: ProductComparePreviewSummaryProps
) {
  const { className, onClearProduct, product } = props;
  const { isMinMd } = useMediaQuery();
  const { isTreadwellFlow, treadwellPreferences } = useTreadwellPreferences();
  const { isFleetAccount } = useMyAccount();

  const isStaggered = Boolean(product?.rearProduct);
  const isWinterOnly = isWinterTire(product);
  const priceInfo = getPriceInfo(product);
  const rearPriceInfo = getPriceInfo(product?.rearProduct);
  const articleStatusProduct = getPrioritizedArticleStatusProduct(
    product,
    product?.rearProduct
  );
  const isNoLongerAvailable = getIsNoLongerAvailable(articleStatusProduct);
  const showMinimumPriceMessage = getShouldShowMinimumPriceMessage(product);
  const showRearMinimumPriceMessage = getShouldShowMinimumPriceMessage(
    product?.rearProduct
  );
  const priceContainerStyleName = `${
    isStaggered ? 'price-container-staggered' : 'price-container'
  }`;
  const isWinterTreadwellFlow = Boolean(treadwellPreferences?.showWinterTires);

  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;

  return (
    <div
      className={`${className || ''} product-compare-preview-summary`}
      styleName="container"
    >
      {product && (
        <div>
          <div styleName="flag-container">
            <div>
              <ProductFlags
                hideLabel
                hideTreadwellRank={!isTreadwellFlow}
                product={product}
              />
              {process.env.__FF_FLEET_ACCOUNT__ === 'true' && (
                <ProductSecondaryFlags
                  hideLabel
                  hideWebOnly
                  product={product}
                />
              )}
            </div>

            {isWinterTreadwellFlow && (
              <div styleName="winter-tire-badge">
                <WinterTireBadge isWinterOnly={isWinterOnly} />
              </div>
            )}
          </div>

          <Button
            icon={TimesRegularIcon}
            onClick={() => onClearProduct?.(product)}
            styleName="delete-button"
          />
        </div>
      )}
      {product && (
        <div>
          <ProductImage
            height={isMinMd ? 72 : 40}
            imageFormatType={
              process.env.__FF_PRODUCT_IMAGE_FORMATS_AUTO_SYNC__ === 'true'
                ? 'PRODUCT_X_SMALL'
                : 'PRODUCT'
            }
            product={product}
            styleName="product-image"
            width={isMinMd ? 72 : 40}
          />

          <div styleName="description">
            <ProductDescription
              brand={product?.brand}
              name={product?.name}
              styleName="product-description"
            />
            {!isNoLongerAvailable && (
              <div styleName={priceContainerStyleName}>
                <div styleName="price-info">
                  <StackedMarkdownPrice
                    hidePercentageSaved={isFleetEnabled}
                    product={product}
                  />
                  <div styleName="price-item">
                    {isStaggered && <div styleName="price-label">F:&nbsp;</div>}

                    <Price
                      {...priceInfo}
                      isPriceForEach
                      seePriceInCartMessage="Price in cart"
                      unitName={product?.unitName}
                    />
                  </div>
                </div>
                {isStaggered && (
                  <div styleName="price-info">
                    <StackedMarkdownPrice
                      hidePercentageSaved={isFleetEnabled}
                      product={product?.rearProduct}
                    />
                    <div styleName="price-item">
                      <div styleName="price-label">R:&nbsp;</div>

                      <Price
                        {...rearPriceInfo}
                        isPriceForEach
                        seePriceInCartMessage="Price in cart"
                        unitName={product?.unitName}
                      />
                    </div>
                  </div>
                )}

                {(showMinimumPriceMessage || showRearMinimumPriceMessage) && (
                  <MinimumPriceMessage
                    message={
                      product?.mapDisplayRule?.message ||
                      product.rearProduct?.mapDisplayRule?.message
                    }
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductComparePreviewSummary;
